import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/app-layout";
import { LoadingOverlay } from "@/components/loading-overlay";
import { Chore, ChoreCompletion, getChore, listChoreCompletions } from "@repo/api/client";
import z from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SquareArrowOutUpLeft } from "lucide-react";
import { BackLink } from "@/components/back-link";
import { CompleteChoreDialog } from "@/components/complete-chore-dialog";
import { Skeleton } from "@/components/ui/skeleton";

const ParamsSchema = z.object({
  choreScheduleId: z.uuid(),
  choreId: z.uuid(),
});

export function ChorePage() {
  const { choreScheduleId, choreId } = ParamsSchema.parse(useParams());

  const choreQuery = useQuery({
    queryKey: ["chore-schedules", choreScheduleId, "chores", choreId],
    queryFn: () => getChore(choreScheduleId, choreId),
  });

  return (
    <LoadingOverlay isLoading={choreQuery.isPending}>
      <AppLayout title={choreQuery.data?.name}>
        <div className="px-4 pt-6 space-y-3">
          <BackLink text="Schedule" to={`/chore-schedules/${choreScheduleId}`} />
          {choreQuery.isError && <ChorePageError />}
          {choreQuery.data && <ChorePageContent chore={choreQuery.data} />}
        </div>
      </AppLayout>
    </LoadingOverlay>
  );
}

function ChorePageError() {
  return <p className="text-sm text-destructive">Failed to load data.</p>;
}

const COMPLETIONS_PAGE_SIZE = 3;

function CompletionRowSkeletons() {
  return (
    <div className="space-y-1">
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-full" />
    </div>
  );
}

function ChoreCompletionsList({ chore }: { chore: Chore }) {
  const { choreScheduleId, id: choreId } = chore;
  const completionsQuery = useInfiniteQuery({
    queryKey: ["chore-schedules", choreScheduleId, "chores", choreId, "completions"],
    queryFn: ({ pageParam }) =>
      listChoreCompletions(choreScheduleId, choreId, {
        limit: COMPLETIONS_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < COMPLETIONS_PAGE_SIZE ? undefined : allPages.length * COMPLETIONS_PAGE_SIZE,
  });

  const completions = completionsQuery.data?.pages.flat() ?? [];
  const { isPending, isFetchingNextPage, hasNextPage } = completionsQuery;

  if (!isPending && completions.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Completions</p>
      {isPending ? (
        <CompletionRowSkeletons />
      ) : (
        <ul className="space-y-1">
          {completions.map((completion) => (
            <CompletionListItem key={completion.id} completion={completion} />
          ))}
        </ul>
      )}
      {isFetchingNextPage && <CompletionRowSkeletons />}
      {(isPending || hasNextPage) && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-muted-foreground"
          disabled={isPending || isFetchingNextPage}
          onClick={() => completionsQuery.fetchNextPage()}
        >
          Load more
        </Button>
      )}
    </div>
  );
}

function ChorePageContent({ chore }: { chore: Chore }) {
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{chore.name}</CardTitle>
          <CardDescription>{chore.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <ChoreCompletionsList chore={chore} />
        </CardContent>
        <CardFooter className="flex-col gap-2">
          <Button className="w-full" onClick={() => setCompleteDialogOpen(true)}>
            Complete
          </Button>
          {chore.googleCalendarHtmlLink ? (
            <Link className="w-full" to={chore.googleCalendarHtmlLink} target="_blank">
              <OpenInGoogleCalendarButton />
            </Link>
          ) : (
            <OpenInGoogleCalendarButton disabled />
          )}
        </CardFooter>
      </Card>
      <CompleteChoreDialog
        chore={chore}
        open={completeDialogOpen}
        onOpenChange={setCompleteDialogOpen}
      />
    </>
  );
}

function CompletionListItem({ completion }: { completion: ChoreCompletion }) {
  const date = completion.completedAt.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <li className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{date}</span>
      {completion.actualDurationMins != null && <span>{completion.actualDurationMins} min</span>}
    </li>
  );
}

function OpenInGoogleCalendarButton({ disabled }: { disabled?: boolean }) {
  return (
    <Button disabled={disabled} variant="outline" className="w-full">
      View in Google Calendar
      <SquareArrowOutUpLeft size={16} className="-scale-x-100 mb-0.5" />
    </Button>
  );
}
